/**
 * User preferences service for managing user-specific settings
 */

import { apiClient } from './api';
import { Shortcut } from '../types/tradeBooking';

export interface DateConventionOverride {
  hasOverride: boolean;
  overrideTradeDate?: string;
  overrideValueDate?: string;
  overrideExpiryDate?: string;
  overridePaymentDate?: string;
}

export interface FixingExpiryConventionOverride {
  hasOverride: boolean;
  overrideFixingReference?: string;
  overrideFixingOffset?: string;
  overrideSettlementType?: string;
  overrideSettlementCurrency?: string;
}

export interface MultiTradeColumnDefaults {
  tradeType?: string;
  direction?: string;
  currency1?: string;
  currency2?: string;
  ourParty?: string;
  counterparty?: string;
  settlementType?: string;
  settlementCurrency?: string;
  fixingReference?: string;
}

export interface MailtoRecipients {
  bcc?: string[];
}

export interface TradeBookingPreferences {
  shortcuts?: Shortcut[];
  dateConventions?: Record<string, DateConventionOverride>;  // Keyed by convention ID
  fixingExpiryConventions?: Record<string, FixingExpiryConventionOverride>;  // Keyed by convention ID
  multiTradeDefaults?: MultiTradeColumnDefaults;
  mailtoRecipients?: MailtoRecipients;
}

export interface UserPreferences {
  tradeBooking?: TradeBookingPreferences;
}

/**
 * Get user preferences from the database
 */
export const getUserPreferences = async (): Promise<UserPreferences> => {
  const response = await apiClient.get<UserPreferences>('/api/v1/users/me/preferences');
  return response.data || {};
};

/**
 * Update user preferences in the database
 */
export const updateUserPreferences = async (preferences: UserPreferences): Promise<UserPreferences> => {
  const response = await apiClient.put<UserPreferences>('/api/v1/users/me/preferences', preferences);
  return response.data;
};

/**
 * Get trade booking shortcuts
 */
export const getTradeBookingShortcuts = async (): Promise<Shortcut[]> => {
  const preferences = await getUserPreferences();
  return preferences.tradeBooking?.shortcuts || [];
};

/**
 * Save trade booking shortcuts
 */
export const saveTradeBookingShortcuts = async (shortcuts: Shortcut[]): Promise<void> => {
  const preferences = await getUserPreferences();

  const updatedPreferences: UserPreferences = {
    ...preferences,
    tradeBooking: {
      ...preferences.tradeBooking,
      shortcuts
    }
  };

  await updateUserPreferences(updatedPreferences);
};

/**
 * Save trade booking date convention overrides
 */
export const saveDateConventionOverrides = async (overrides: Record<string, DateConventionOverride>): Promise<void> => {
  const preferences = await getUserPreferences();

  const updatedPreferences: UserPreferences = {
    ...preferences,
    tradeBooking: {
      ...preferences.tradeBooking,
      dateConventions: overrides
    }
  };

  await updateUserPreferences(updatedPreferences);
};

/**
 * Save trade booking fixing/expiry convention overrides
 */
export const saveFixingExpiryConventionOverrides = async (overrides: Record<string, FixingExpiryConventionOverride>): Promise<void> => {
  const preferences = await getUserPreferences();

  const updatedPreferences: UserPreferences = {
    ...preferences,
    tradeBooking: {
      ...preferences.tradeBooking,
      fixingExpiryConventions: overrides
    }
  };

  await updateUserPreferences(updatedPreferences);
};

/**
 * Get multi-trade column defaults
 */
export const getMultiTradeDefaults = async (): Promise<MultiTradeColumnDefaults> => {
  const preferences = await getUserPreferences();
  return preferences.tradeBooking?.multiTradeDefaults || {};
};

/**
 * Save multi-trade column defaults
 */
export const saveMultiTradeDefaults = async (defaults: MultiTradeColumnDefaults): Promise<void> => {
  const preferences = await getUserPreferences();

  const updatedPreferences: UserPreferences = {
    ...preferences,
    tradeBooking: {
      ...preferences.tradeBooking,
      multiTradeDefaults: defaults
    }
  };

  await updateUserPreferences(updatedPreferences);
};

/**
 * Get mailto recipients (BCC list)
 */
export const getMailtoRecipients = async (): Promise<string[]> => {
  const preferences = await getUserPreferences();
  return preferences.tradeBooking?.mailtoRecipients?.bcc || [];
};

/**
 * Save mailto recipients (BCC list)
 */
export const saveMailtoRecipients = async (bcc: string[]): Promise<void> => {
  const preferences = await getUserPreferences();

  const updatedPreferences: UserPreferences = {
    ...preferences,
    tradeBooking: {
      ...preferences.tradeBooking,
      mailtoRecipients: { bcc }
    }
  };

  await updateUserPreferences(updatedPreferences);
};
